import { API_MODE } from '@/api'
import { HEADER_ENTRIES } from '@/config/nav'
import { FONT_SIZES, THEMES, useSettings } from '@/context/SettingsContext'
import { useNavigate } from 'react-router-dom'
import { Icon } from './icons'
import { Badge, Button, Field, Input, Modal, Segmented } from './ui'
import { useState } from 'react'

/**
 * 设置弹窗承载的那两条页面入口（`surface: 'settings'`，2026-10-09 加）。
 *
 * 为什么不在这里写死路径：那是**第二份清单**。改了顶栏配置里的 `to` 而这里没跟着改，
 * 症状是「点了跳到一个已经不存在的地址」—— 而两处都写着路径时，没有任何测试会红。
 * 闸门里有一条与这条配套的断言：**设置弹窗里不许出现这些路径的字面量**
 * （见 scripts/smoke-nav.ts 的 settings 段）。
 */
const MY_ENTRIES = HEADER_ENTRIES.filter((e) => e.surface === 'settings')

export function SettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { settings, setTheme, setFontSize, toggleSidebar, setCompactTable, resetAll } = useSettings()
  const [testing] = useState(false)
  const [testResult, setTestResult] = useState<string | null>(null)
  const navigate = useNavigate()

  /*
   * 先关弹窗、再跳页。
   *
   * ⚠️ 顺序不能反过来，也不能只做一件事：弹窗是盖在整页之上的遮罩层
   * （`--z-settings` 那一档），只跳页不关的话，路由已经换了、页面也被盖着 ——
   * 用户看到的就是「点了没反应，设置还开着」。反过来先关再跳，跳完就是干净的页面。
   */
  const goto = (to: string) => {
    onClose()
    navigate(to)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="设置"
      icon="settings"
      width={620}
      footer={
        <>
          <Button
            variant="danger"
            onClick={() => {
              resetAll()
              setTestResult(null)
            }}
          >
            恢复默认
          </Button>
          <div className="grow" />
          <Button variant="primary" onClick={onClose}>
            完成
          </Button>
        </>
      }
    >
      <div className="stack--lg stack">
        {/*
          「我的」放在**最上面**，而不是顺着设置项排到最后。

          这一块的语义与下面那些设置项不同：它们是**页面入口**（点了就走），
          下面的是**可选可改的偏好**。摆在最底下的话，用户要先滚过主题墙、
          字号、布局、数据层才看得见它 —— 而它恰恰是「刚从顶栏搬过来的入口」，
          第一眼找不到就等于被藏起来了。
        */}
        {MY_ENTRIES.length ? (
          <div className="card" style={{ boxShadow: 'none', background: 'hsl(var(--muted) / .5)' }}>
            <div className="card__body card__body--tight stack stack--sm">
              <div className="row row--between">
                <span className="text-sm" style={{ fontWeight: 600 }}>
                  <Icon name="folder" size={14} /> 我的
                </span>
              </div>
              <div className="text-xs muted" style={{ lineHeight: 1.6 }}>
                回看产出与花费。这两页原先在顶栏常驻，现在挪到这里 ——
                顶栏那一排留给「今天要出什么素材」。
              </div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                {MY_ENTRIES.map((e) => (
                  <Button
                    key={e.id}
                    size="sm"
                    icon={e.icon}
                    title={e.hint}
                    onClick={() => goto(e.to)}
                    data-settings-entry={e.id}
                  >
                    {e.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {/* 主题 */}
        <div>
          <div className="field__label" style={{ marginBottom: 10 }}>
            <Icon name="sun" size={14} /> 主题配色
          </div>
          <div className="theme-grid">
            {THEMES.map((t) => (
              <button
                key={t.id}
                type="button"
                className="theme-card"
                data-active={settings.theme === t.id}
                onClick={() => setTheme(t.id)}
              >
                <div className="theme-card__swatch">
                  {t.swatch.map((c) => (
                    <span key={c} style={{ background: c }} />
                  ))}
                </div>
                <div className="theme-card__name">{t.name}</div>
                <div className="theme-card__desc">{t.desc}</div>
                {settings.theme === t.id ? (
                  <span className="theme-card__check">
                    <Icon name="check" size={12} />
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        {/* 字号 */}
        <Field label="界面字号" hint="影不影响浏览器缩放，只调整工作台内部字号基准">
          <Segmented
            value={settings.fontSize}
            onChange={setFontSize}
            options={FONT_SIZES.map((f) => ({ value: f.id, label: `${f.name} ${f.px}px` }))}
          />
        </Field>

        {/* 布局 */}
        <Field label="布局">
          <div className="stack stack--sm">
            <label className="toggle">
              <input type="checkbox" checked={settings.sidebarCollapsed} onChange={toggleSidebar} />
              <span>默认折叠侧边栏（只显示图标）</span>
            </label>
            <label className="toggle">
              <input
                type="checkbox"
                checked={settings.compactTable}
                onChange={(e) => setCompactTable(e.target.checked)}
              />
              <span>表格紧凑模式</span>
            </label>
          </div>
        </Field>

        {/* 第三方 LLM —— **已下线**，见下面这段说明。 */}
        <div>
          <div className="field__label" style={{ marginBottom: 4 }}>
            <Icon name="bolt" size={14} /> 文案模型
          </div>
          <div className="field__hint">
            改到「后台管理 → 上游通道」了（仅管理员可见，顶栏右侧）。地址 / Key / 模型名改完
            **立即对后端生效**，所有账号共用。
          </div>
        </div>

        {/* 数据层 */}
        <div className="card" style={{ boxShadow: 'none', background: 'hsl(var(--muted) / .5)' }}>
          <div className="card__body card__body--tight stack stack--sm">
            <div className="row row--between">
              <span className="text-sm" style={{ fontWeight: 600 }}>
                数据层
              </span>
              <Badge tone={API_MODE === 'mock' ? 'warning' : 'success'}>
                {API_MODE === 'mock' ? 'VITE_API_MODE=mock' : 'VITE_API_MODE=http'}
              </Badge>
            </div>
            <div className="text-xs muted" style={{ lineHeight: 1.6 }}>
              当前为{API_MODE === 'mock' ? '原型演示模式，所有生成结果由本地模拟' : '真实后端模式，请求同源 /api/*'}。
              把 <span className="mono">.env.development</span> 里的 <span className="mono">VITE_API_MODE</span> 改成{' '}
              <span className="mono">http</span> 并启动 Python 后端即可切换，前端代码零改动。
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
